import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';

export default function QuickContact() {
  const { t } = useTranslation('contact');
  const { info } = useCompanyInfo();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const cards = [
    {
      icon: 'ri-mail-line',
      title: t('quick.email.label'),
      value: info.email,
      desc: t('quick.email.desc'),
      href: `mailto:${info.email}`,
    },
    {
      icon: 'ri-phone-line',
      title: t('quick.phone.label'),
      value: info.phoneKr,
      valueAlt: info.phoneCn,
      desc: t('quick.phone.desc'),
      href: `tel:${info.phoneKr.replace(/\s/g, '')}`,
    },
    {
      icon: 'ri-time-line',
      title: t('quick.response.label'),
      value: t('quick.response.value'),
      desc: t('quick.response.desc'),
      href: null,
    },
  ];

  return (
    <section className="w-full bg-white py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-3">
              {t('quick.label')}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-800 mb-3">
              {t('quick.title')}
            </h2>
            <p className="text-sm md:text-base text-ink-500 max-w-2xl mx-auto leading-relaxed">
              {t('quick.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {cards.map((card, idx) => {
              const inner = (
                <>
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-cream-100 flex items-center justify-center mb-4 rounded-lg">
                    <i className={`${card.icon} text-ink-700 text-xl md:text-2xl`} />
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-ink-700 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-base md:text-lg font-bold text-ink-900 mb-1">{card.value}</p>
                  {card.valueAlt && (
                    <p className="text-base md:text-lg font-bold text-ink-900 mb-1">{card.valueAlt}</p>
                  )}
                  <p className="text-xs md:text-sm text-ink-400 leading-relaxed">{card.desc}</p>
                </>
              );

              const className = `bg-cream-50 rounded-xl border border-cream-200 p-6 md:p-7 text-center transition-all duration-500 ${
                mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`;

              if (card.href) {
                return (
                  <a
                    key={card.title}
                    href={card.href}
                    style={{ transitionDelay: `${idx * 80}ms` }}
                    className={`${className} hover:border-warm-300 hover:bg-cream-100 cursor-pointer`}
                  >
                    {inner}
                  </a>
                );
              }
              return (
                <div key={card.title} style={{ transitionDelay: `${idx * 80}ms` }} className={className}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}